import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Add, ChevronRightRounded, OpenInNew} from "@mui/icons-material";
import Button from "../../../../../../components/base/button/button";
import {routeFunctions, routes} from "../../../../../../../core/models/static/routes/app-routes";
import InventoryAdjustmentListSearchSection from "../../../../../../components/search-sections/inventory-adjustment-list-search-section";
import {DefaultPaginationInfo} from "../../../../../../../core/constants/enums";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {InventoryAdjustmentListDataGridCellKeys} from "../../../../../../../core/constants/table/data-grid-column-keys";
import {dataGridCellTextProps, DataGridColumnAlignments, DataGridColumnTypes} from "../../../../../../../packages/data-grid";
import IconButton from "../../../../../../components/base/button/icon_button";
import classnames from "classnames";
import PartnerDataGrid, {useDataGridSavedState} from "../../../../../../containers/data-grid";
import SimpleMoreDetailsDialog from "../../../../../../components/dialogs/simple-more-details-dialog";
import Api from "../../../../../../../core/services/api/api";
import {formatMoney} from "../../../../../../../core/services/utils/utils";
import useSearch from "../../../../../../hooks/use-search";
import {useConfigureNotebook} from "../../../../../../hooks/use-notebook-config";
import DateUtils from "../../../../../../../core/services/date-utils";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import ImportAdjustmentDialog from "../../../../../../components/dialogs/import-adjustment-dialog";
import {SvgIcon} from "@mui/material";
import {ReactComponent as Excel} from "../../../../../../../assets/images/excel.svg";
import {Link} from "react-router-dom";
import useBehavioralAnalyticsTool from "../../../../../../hooks/use-behavioural-analytics-tool";


const dataGridSaveConfig = {
    name: 'InventoryAdjustmentListViewDataGrid',
    version: 4,
}

const exportingChunkSize = 100;

const initialImportAdjustmentDialogState = {
    open: false,
    close: () => void 0,
}

const InventoryAdjustmentListView = () => {
    const [data, setData] = useState([]);
    const [fullNoteDialogOpen, setFullNoteDialogOpen] = useState(false);
    const [fullNote, setFullNote] = useState(null);
    const [importAdjustmentDialogState, setImportAdjustmentDialogState] = useState(initialImportAdjustmentDialogState);
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('inventory-adjustment-list-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const operatorAccessLevels = useAccessLevels();
    const canExportInventoryAdjustments = operatorAccessLevels.packages.bizInventory.exportInventoryAdjustments;

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.inventoryAdjustmentList);
        configureBehavioralTool((tags) => tags.inventoryAdjustmentList);
    }, [configureBehavioralTool, configureNotebookTags]);

    /**
     * Memo version of columns entities used in data grid
     * @type {DataGridColumn[]}
     */
    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            ...(operatorAccessLevels.packages.bizInventory.adjustmentInformation ? [
                {
                    name: InventoryAdjustmentListDataGridCellKeys.view,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    pinned: false,
                    sortable: false,
                    resizable: false,
                    visibilityToggleable: false,
                    width: 0,
                },
            ] : []),
            {
                title: "Code",
                name: InventoryAdjustmentListDataGridCellKeys.code,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                width: 100,
            },
            {
                title: "Type",
                name: InventoryAdjustmentListDataGridCellKeys.type,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                width: 60,
            },
            {
                title: "Submitted Date",
                name: InventoryAdjustmentListDataGridCellKeys.dateTime,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.dateTime,
                width: 140,
            },
            {
                title: "Product Count",
                name: InventoryAdjustmentListDataGridCellKeys.productCount,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.string,
                width: 110,
            },
            {
                title: "Total Product Count",
                name: InventoryAdjustmentListDataGridCellKeys.totalCount,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.string,
                width: 110,
            },
            {
                title: "Average Cost",
                name: InventoryAdjustmentListDataGridCellKeys.totalCost,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                width: 100,
            },
            {
                title: "Operator",
                name: InventoryAdjustmentListDataGridCellKeys.operator,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                width: 100,
                sortable: false,
            },
            {
                title: "Note",
                name: InventoryAdjustmentListDataGridCellKeys.note,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                width: 400,
                sortable: false,
            },
        ], [operatorAccessLevels.packages.bizInventory.adjustmentInformation])

    const [dataGridColumns, dataGridSortBy, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    /**
     * Prepares teh api request body
     * @param paginationInfo
     * @type {(function(UseSearchFunctionArg): any)}
     */
    const prepareApiRequest = useCallback((args) => {
        let forApi = {};
        if (args.orderBy?.field) {
            forApi['sorts'] = [
                {
                    property: args.orderBy.field,
                    isDesc: args.orderBy.descending,
                }
            ]
        }
        forApi = {
            ...forApi,
            pagination: args.paginationInfo,
            keyword: args.filters?.keyword?.toString() ?? null,
            isAdd: (!args.filters?.type || args.filters.type === 0) ? null : args.filters.type === 1,
            startDateTime: args.filters.startDate,
            endDateTime: args.filters.endDat,
        }
        return forApi;
    }, [])

    /**
     * Fetches the list of inventory adjustments from server
     * @type {(function(UseSearchFunctionArg, boolean=): Promise<undefined|ApiResponseDs<*>>)}
     */
    const fetchEntries = useCallback(async (args, forExport = false) => {
        const forApi = prepareApiRequest(args);
        const response = await Api.getInventoryAdjustmentList(forApi, args.cancelTokenSource?.token);
        if (!isMounted())
            return;
        if (response?.resultFlag && !forExport) {
            setData(response?.data?.items ?? []);
            args.setPaginationInfo(prevState => ({
                ...prevState,
                length: response?.data?.totalCount ?? 0
            }))
        }
        if (response?.resultFlag && forExport)
            return response;
    }, [isMounted, prepareApiRequest])

    /**
     * Searches among the entries based on the filters from the user.
     * @type {UseSearchFunction}
     */
    const search = useCallback(async (result) => {
        await fetchEntries(result, false);
    }, [fetchEntries]);

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ...DefaultPaginationInfo,
        pageSize: dataGridPageSize
    } : DefaultPaginationInfo, [dataGridPageSize])

    const initialSearchDataSettings = useMemo(() => ({
        paginationInfo: initialPaginationInfo,
        orderBy: dataGridSortBy,
    }), [initialPaginationInfo, dataGridSortBy]);

    const searchData = useSearch(search, initialSearchDataSettings)

    /**
     * Formats the list of lost search reports into csv format
     * @param list
     * @return {{title: string, body: (*|*[])}}
     */
    const formatExportingData = useCallback((list) => {
        const exportTitle = `Inventory Adjustment List`;
        return {
            title: exportTitle,
            body: list?.map((item, index) => ({
                "": index + 1,
                'Code': item?.code,
                'Type': item?.isAdd ? 'Add' : 'Remove',
                'Submitted Date': item?.submittedDatetime ? DateUtils.serverToLocal(item?.submittedDatetime).format(DateUtils.dateTimeFormat) : "--",
                'Product Count': item?.productCount ? Number(item?.productCount) : "--",
                'Total Product Count': item?.totalProductCount ? Number(item?.totalProductCount) : "--",
                'Total Cost': item?.totalCost ? formatMoney(item?.totalCost) : "--",
                'Operator Name': item?.submittedBy,
                'Note': item?.description
            })) ?? [],
        }
    }, [])

    /**
     * Exports the requests using the same search filters to csv
     */
    const exportToCsv = useCallback(async () => {
        const _paginationInfo = {
            length: searchData.paginationInfo.length,
            pageSize: exportingChunkSize,
            currentPage: 1,
        };
        let entries = [];
        while (searchData.paginationInfo.length > entries.length) {
            const res = await fetchEntries({...searchData, paginationInfo: _paginationInfo}, true);
            if (!res?.resultFlag) {
                return;
            }
            if (!res?.data?.items?.length) {
                break;
            }
            entries.push(...res.data.items);
            if (!isMounted())
                return;
            _paginationInfo.currentPage++;
        }
        return formatExportingData(entries);
    }, [fetchEntries, formatExportingData, isMounted, searchData])

    /**
     * Opens a dialog for showing the note full details
     * @param note
     */
    const viewFullNote = (note) => {
        setFullNote(note);
        setFullNoteDialogOpen(true);
    }

    /**
     * Memo version of data grid Rows
     * @type {DataGridRow[]}
     */
    const rows = useMemo(() => {
        return data?.map((record) => ({
            key: record.id,
            data: record,
            cells: {
                [InventoryAdjustmentListDataGridCellKeys.view]:
                    <IconButton
                        color={'secondary'}
                        component={Link}
                        to={routeFunctions.inventory.inventoryAdjustment.single(record.id)}
                    >
                        <OpenInNew className='icon normal'/>
                    </IconButton>,
                [InventoryAdjustmentListDataGridCellKeys.code]: record?.code ?? "",
                [InventoryAdjustmentListDataGridCellKeys.type]: record?.isAdd ? 'Add' : 'Remove',
                [InventoryAdjustmentListDataGridCellKeys.dateTime]: record?.submittedDatetime,
                [InventoryAdjustmentListDataGridCellKeys.productCount]: record?.productCount ? Number(record?.productCount) : "--",
                [InventoryAdjustmentListDataGridCellKeys.totalCount]: record?.totalProductCount ? Number(record?.totalProductCount) : "--",
                [InventoryAdjustmentListDataGridCellKeys.totalCost]: record?.totalCost ? formatMoney(record?.totalCost) : "--",
                [InventoryAdjustmentListDataGridCellKeys.operator]: record?.submittedBy,
                [InventoryAdjustmentListDataGridCellKeys.note]: record?.description?.length > 30
                    ? <div className={'note-container'}>
                        <p {...dataGridCellTextProps} className={'note'}>
                            {`${record?.description.substring(0, 25)}...`}
                        </p>
                        <div
                            className={classnames('part-number-text-button')}
                            onClick={() => viewFullNote(record?.description)}
                        >
                            <p className={'mb-1'}>More</p>
                            <ChevronRightRounded className='icon small' color={"primary"}/>
                        </div>
                    </div>
                    :
                    <p {...dataGridCellTextProps}>{record?.description}</p>,
            }
        }))
    }, [data])

    /**
     * Opens the import adjustment dialog
     * @type {(function(): void)}
     */
    const importAdjustment = useCallback(() => {
        setImportAdjustmentDialogState({
            open: true,
            close: () => setImportAdjustmentDialogState(initialImportAdjustmentDialogState)
        })
    }, [])

    const addImportedAdjustments = useCallback((adjustments) => {
        setData(prevState => [...adjustments, ...prevState]);
    }, [])

    return (
        <>
            <div className={'inventory-adjustment-list-view'}>
                <div className={'d-flex flex-row justify-content-between align-items-center fast-connection-view w-100 px-2 mt-3 mb-4'}>
                    <p className={'view-title'}>
                        Inventory Adjustment List
                    </p>
                    {
                        operatorAccessLevels.packages.bizInventory.submitAdjustment &&
                        <div className={'d-flex align-items-center justify-content-end'}>
                            <Button
                                style={{height: '45px'}}
                                className='mr-2 d-flex justify-content-center align-items-center'
                                variant={'outlined'}
                                color={'primary'}
                                onClick={importAdjustment}
                            >
                                <SvgIcon className='icon normal' component={Excel}/>
                                <p className='ml-2'>Import from Excel</p>
                            </Button>
                            <Button
                                style={{height: '45px'}}
                                className='d-flex justify-content-center align-items-center'
                                variant={'outlined'}
                                color={'primary'}
                                component={Link}
                                to={routes.inventory.inventoryAdjustment.bulkAvailabilityChange}
                            >
                                <Add className='icon small'/>
                                <p className='ml-2'>Add a new Adjustment</p>
                            </Button>
                        </div>
                    }
                </div>
                <InventoryAdjustmentListSearchSection searchData={searchData}/>
                <div className={'mb-5'}/>
                <PartnerDataGrid
                    {...dataGridSaveConfig}
                    initialLayoutState={{
                        columns: columns,
                    }}
                    columns={dataGridColumns}
                    rows={rows}
                    minRowHeight={48}
                    state={{
                        sortBy: searchData.orderBy,
                        pagination: searchData.paginationInfo,
                        loading: {state: searchData.loading},
                        density: dataGridDensity,
                    }}
                    hideFooterActions
                    getExportInfo={canExportInventoryAdjustments ? exportToCsv : undefined}
                    onSortByChanged={(_, current) => searchData.setOrderBy(current)}
                    onCurrentPageChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                    onPageSizeChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                />
            </div>
            <SimpleMoreDetailsDialog
                open={fullNoteDialogOpen}
                setOpen={setFullNoteDialogOpen}
                title={'Note'}
                details={fullNote}
            />
            <ImportAdjustmentDialog
                open={importAdjustmentDialogState.open}
                close={importAdjustmentDialogState.close}
                onSuccess={addImportedAdjustments}
            />
        </>
    );
}

export default InventoryAdjustmentListView;
